import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import userService from "./userService";
import { IUser } from "../../types/user";


interface UserState {
    user: IUser | null;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    message: string | null;
}

// Load persisted user data if available
const persistedState = JSON.parse(localStorage.getItem("persist:root") || "{}");
const auth = persistedState.auth ? JSON.parse(persistedState.auth) : {};

const initialState: UserState = {
    user: auth.user ? auth.user : null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: null,
};

// **Update User Details**
export const updateUser = createAsyncThunk(
    "user/update",
    async ({ userData, token }: { userData: Partial<IUser>, token: string }, thunkAPI) => {
        try {
            return await userService.updateUser(userData, token);
        } catch (error: any) {
            const message =
                error.response?.data?.message || error.message || "Failed to update user";
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        resetUserState: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateUser.fulfilled, (state, action: PayloadAction<IUser>) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.user = action.payload;
                localStorage.setItem(
                    "persist:root",
                    JSON.stringify({
                        auth: {
                            ...state,
                            user: action.payload,
                        },
                    })
                );
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            });
    },
});

export const { resetUserState } = userSlice.actions;
export default userSlice.reducer;
